import { Store } from "@tanstack/store";
import { useSelector } from "@tanstack/react-store";
import useNetwork from "@/hooks/sensors/useNetwork";
import { useQueue } from "./queue-store";
import type { PauseReason, QueueState } from "./upload-queue";

const idleQueueState = new Store<QueueState>({ paused: false, pauseReason: null });

/** Pause state of whichever queue instance is live, or a standing idle
 * state before a scanner name exists and there is no queue to ask. */
export function useQueueState(): QueueState {
  const queue = useQueue();
  return useSelector(queue?.stateStore ?? idleQueueState);
}

/** Shared across HomePage and ScriptDetailPage so a paused/offline queue is
 * visible from wherever the scanner actually is, not just Home. */
export function useQueueBannerText(hasPendingWork: boolean): string | null {
  const queueState = useQueueState();
  const { online } = useNetwork();
  return bannerText(hasPendingWork, online ?? true, queueState.paused, queueState.pauseReason);
}

export function bannerText(
  hasPendingWork: boolean,
  online: boolean,
  paused: boolean,
  pauseReason: PauseReason | null,
): string | null {
  if (!online) return "You're offline — uploads will resume when you're back online.";
  if (paused) {
    if (pauseReason === "auth") return "Upload paused: authentication failed.";
    if (pauseReason === "unreachable") return "Upload paused: server unreachable.";
    if (pauseReason === "offline") return "Upload paused: you're offline.";
  }
  if (hasPendingWork) return "Uploading in the background…";
  return null;
}
